import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdTextField,
} from "../../ipd/utils";
import { getBedOccupancy } from "../api/emergencyApi";
import {
  EMERGENCY_BED_SERVICE_TYPE,
  captureEmergencyCharges,
  openEmergencyBilling,
  searchChargeableServices,
} from "../api/emergencyBillingApi";
import { formatINR, lineGross, pickBedService, summarizeCapture } from "../utils/charges";

const DEBOUNCE_MS = 300;

/**
 * Putting charges on an emergency visit.
 *
 * Several lines are staged and posted in one call, because a bed charge plus the
 * two procedures that went with it is one billing decision. Each line still
 * succeeds or fails on its own server-side, so a partial post keeps the dialog
 * open showing exactly which lines were refused and why, rather than closing on
 * a half-done set.
 *
 * The billing account is opened on the way to the first charge instead of behind
 * a separate button: the call is idempotent, it is mandatory before any charge,
 * and a clerk should not have to know that billing accounts exist.
 */

const blankLine = (service, key) => ({
  key,
  id: service.id,
  code: service.code,
  name: service.name,
  fee: service.fee,
  unit: service.unit,
  quantity: 1,
  amount: "",
  remarks: "",
  error: "",
});

/** A line the server would refuse: an unpriced service needs an amount. */
const lineReady = (line) =>
  Boolean(line.code || line.id) &&
  Number(line.quantity) > 0 &&
  (line.fee !== null || (line.amount !== "" && Number.isFinite(Number(line.amount))));

export default function AddChargeDialog({ open, visit, onClose, onCaptured }) {
  const visitId = visit?.id;

  const [scope, setScope] = useState("bed");
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  const [bedServices, setBedServices] = useState([]);
  const [lines, setLines] = useState([]);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const keyRef = useRef(0);
  const reqRef = useRef(0);

  const addLine = useCallback((service) => {
    setLines((current) =>
      current.some((l) => l.code && l.code === service.code)
        ? current
        : [...current, blankLine(service, `line-${++keyRef.current}`)]
    );
  }, []);

  /* -------------------------------------------------- open: preload + prefill */
  useEffect(() => {
    if (!open) return undefined;
    let live = true;

    setScope("bed");
    setQuery("");
    setOptions([]);
    setLines([]);
    setError("");

    (async () => {
      let beds = [];
      try {
        beds = await searchChargeableServices({
          serviceType: EMERGENCY_BED_SERVICE_TYPE,
        });
      } catch (e) {
        // Not fatal: the clerk can still search. No bed charges configured on a
        // tenant is a setup gap, not a reason to block charging anything at all.
        if (live) setError(e?.message || "Could not load the emergency bed charges");
      }
      if (!live) return;
      setBedServices(beds);
      setOptions(beds);

      // Preselect the bed the patient is actually in. Needs the bed's category,
      // which the visit does not carry — the occupancy list is the only place
      // that maps bed to category today.
      if (!visit?.bed_id || beds.length === 0) return;
      try {
        const occupancy = await getBedOccupancy();
        const bed = (occupancy?.beds || []).find(
          (b) => String(b.id) === String(visit.bed_id)
        );
        const match = pickBedService(beds, bed?.category_id);
        if (live && match) addLine(match);
      } catch {
        // Prefill is a convenience. Losing it is not worth an error message.
      }
    })();

    return () => {
      live = false;
    };
  }, [open, visit, addLine]);

  /* ------------------------------------------------------- debounced search */
  useEffect(() => {
    if (!open) return undefined;
    const term = query.trim();

    // Empty box in bed scope shows the preloaded bed charges, which is the
    // whole list — searching for nothing would just fetch it again.
    if (!term) {
      setOptions(scope === "bed" ? bedServices : []);
      return undefined;
    }

    const reqId = ++reqRef.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await searchChargeableServices({
          name: term,
          ...(scope === "bed" ? { serviceType: EMERGENCY_BED_SERVICE_TYPE } : {}),
        });
        if (reqRef.current === reqId) setOptions(rows);
      } catch {
        if (reqRef.current === reqId) setOptions([]);
      } finally {
        if (reqRef.current === reqId) setSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, scope, open, bedServices]);

  const patchLine = (key, patch) =>
    setLines((current) =>
      current.map((l) => (l.key === key ? { ...l, ...patch, error: "" } : l))
    );

  const removeLine = (key) =>
    setLines((current) => current.filter((l) => l.key !== key));

  const total = lines.reduce((sum, l) => sum + lineGross(l), 0);
  const ready = lines.length > 0 && lines.every(lineReady);

  const submit = async () => {
    setPosting(true);
    setError("");
    try {
      // Mandatory before any charge and idempotent, so it is safe to send every
      // time rather than tracking whether this visit has been set up before.
      if (!visit?.billing_account_id) await openEmergencyBilling(visitId);

      const body = await captureEmergencyCharges(visitId, lines);
      const { posted, failed } = summarizeCapture(body, lines);

      if (failed.length === 0) {
        onCaptured?.(body.message || `${posted.length} charge(s) captured.`);
        onClose();
        return;
      }

      // Keep only what was refused, carrying its reason, so the clerk corrects
      // the failed lines instead of re-entering the whole set.
      const failedKeys = new Map(failed.map((f) => [f.line?.key, f.error]));
      setLines((current) =>
        current
          .filter((l) => failedKeys.has(l.key))
          .map((l) => ({ ...l, error: failedKeys.get(l.key) }))
      );
      setError(
        posted.length
          ? `${posted.length} charge(s) captured. ${failed.length} were refused — correct them below.`
          : "None of the charges were accepted."
      );
      // Null message: the ledger must refresh, but the warning above already
      // says what happened — a success toast on top of it would contradict it.
      if (posted.length) onCaptured?.(null);
    } catch (e) {
      setError(e?.message || "Could not capture the charges");
    } finally {
      setPosting(false);
    }
  };

  if (!visit) return null;

  return (
    <Dialog
      open={open}
      onClose={posting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${IPD_SHAPE.radiusLg}px` } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
          Add charges
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
          {visit.bed_label
            ? `${visit.patient_name || "This patient"} · ${visit.bed_label}`
            : visit.patient_name || "This visit"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert
            severity={lines.some((l) => l.error) ? "warning" : "error"}
            sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {/* ------------------------------------------------------- the picker */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Search in</Typography>
            <IpdTextField
              select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              size="small"
              fullWidth
              sx={ipdElevatedInputSx}
              SelectProps={{ MenuProps: getIpdSelectMenuProps() }}
              inputProps={{ "aria-label": "Search in" }}
            >
              <MenuItem value="bed">Emergency bed charges</MenuItem>
              <MenuItem value="all">All services</MenuItem>
            </IpdTextField>
          </Box>

          <Box>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Service</Typography>
            <Autocomplete
              size="small"
              options={options}
              loading={searching}
              inputValue={query}
              value={null}
              blurOnSelect
              clearOnBlur
              getOptionLabel={(o) => o?.name || ""}
              isOptionEqualToValue={(a, b) => a?.code === b?.code}
              filterOptions={(x) => x}
              onInputChange={(_, v, reason) => {
                if (reason !== "reset") setQuery(v);
              }}
              onChange={(_, v) => {
                if (v) {
                  addLine(v);
                  setQuery("");
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.code || option.id}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                      {option.code}
                      {option.unit ? ` · per ${option.unit}` : ""}
                      {" · "}
                      {option.fee === null ? "no preset fee" : formatINR(option.fee)}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText={
                searching
                  ? "Searching…"
                  : scope === "bed"
                  ? "No emergency bed charges configured"
                  : "Type to search the service catalogue"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search a service to charge…"
                  sx={ipdElevatedInputSx}
                  inputProps={{ ...params.inputProps, "aria-label": "Service" }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searching ? <CircularProgress size={15} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* -------------------------------------------------- the staged lines */}
        {lines.length === 0 ? (
          <Box
            sx={{
              mt: 2,
              textAlign: "center",
              py: 3,
              px: 2,
              border: `1px dashed ${IPD_COLORS.borderHover}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
              Nothing staged yet
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
              Pick a service above. Add as many as belong on this visit — they are
              posted together.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {lines.map((line) => (
              <Box
                key={line.key}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: `${IPD_SHAPE.radiusMd}px`,
                  border: `1px solid ${
                    line.error ? IPD_COLORS.error.text : IPD_COLORS.border
                  }`,
                  bgcolor: IPD_COLORS.cardBg,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                      {line.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                      {line.code}
                      {line.unit ? ` · per ${line.unit}` : ""}
                      {" · "}
                      {line.fee === null
                        ? "no preset fee — enter an amount"
                        : `${formatINR(line.fee)} each`}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", mt: 0.2 }}
                  >
                    {formatINR(lineGross(line))}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${line.name}`}
                    onClick={() => removeLine(line.key)}
                    disabled={posting}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18, color: IPD_COLORS.textMuted }} />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    mt: 1.25,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "110px 150px 1fr" },
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>Quantity</Typography>
                    <IpdTextField
                      type="number"
                      value={line.quantity}
                      onChange={(e) => patchLine(line.key, { quantity: e.target.value })}
                      size="small"
                      fullWidth
                      sx={ipdElevatedInputSx}
                      inputProps={{ min: 1, step: 1, "aria-label": `Quantity for ${line.name}` }}
                      disabled={posting}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>
                      Amount (₹)
                      {line.fee === null && (
                        <span style={{ color: IPD_COLORS.error.text }}> *</span>
                      )}
                    </Typography>
                    <IpdTextField
                      type="number"
                      value={line.amount}
                      onChange={(e) => patchLine(line.key, { amount: e.target.value })}
                      placeholder={line.fee === null ? "Required" : "Catalogue fee"}
                      size="small"
                      fullWidth
                      sx={ipdElevatedInputSx}
                      inputProps={{ min: 0, "aria-label": `Amount for ${line.name}` }}
                      disabled={posting}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>Remarks (optional)</Typography>
                    <IpdTextField
                      value={line.remarks}
                      onChange={(e) => patchLine(line.key, { remarks: e.target.value })}
                      placeholder="Why this was charged"
                      size="small"
                      fullWidth
                      sx={ipdElevatedInputSx}
                      inputProps={{ "aria-label": `Remarks for ${line.name}` }}
                      disabled={posting}
                    />
                  </Box>
                </Box>

                {line.error && (
                  <Typography
                    sx={{ mt: 1, fontSize: 12.5, fontWeight: 600, color: IPD_COLORS.error.text }}
                  >
                    {line.error}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
          {lines.length > 0 && `Total ${formatINR(total)}`}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={posting}>
            Cancel
          </IpdButton>
          <IpdButton onClick={submit} loading={posting} disabled={!ready}>
            {lines.length > 1 ? `Post ${lines.length} charges` : "Post charge"}
          </IpdButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
